import React, { useState } from 'react';
import { MessageCircle, ShoppingCart, HelpCircle, ChevronUp, Globe, Truck, PackageCheck, Info } from 'lucide-react';
import { Button } from './ui/button';

const MedicineRow = ({ medicine, onWhatsAppClick }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-white border-b border-gray-100 hover:bg-slate-50 transition-all overflow-hidden">
            {/* Main Row */}
            <div className="py-7 px-4 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Medicine Info */}
                <div className="flex-1 w-full text-center md:text-left">
                    <div className="flex flex-col md:flex-row md:items-baseline gap-2 mb-2">
                        <h3 className="text-2xl font-black text-slate-900 tracking-tight leading-none mb-1">{medicine.name}</h3>
                    </div>
                    <p className="text-sm font-bold text-[#D92D20] mb-4 uppercase tracking-wide">{medicine.indication}</p>
                    <div className="flex items-center justify-center md:justify-start gap-4 text-[11px] text-slate-400 font-black tracking-widest">
                        <div className="flex items-center gap-1.5 ">
                            <span className="opacity-40 uppercase">PACK:</span>
                            <span className="text-slate-600 font-bold">{medicine.packSize}</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-slate-200 rounded-full"></div>
                        <div className="flex items-center gap-1.5">
                            <span className="opacity-40 uppercase">STRENGTH:</span>
                            <span className="text-slate-600 font-bold">{medicine.strength}</span>
                        </div>
                    </div>
                </div>

                {/* Pricing Section */}
                <div className="flex items-center gap-10 text-center md:text-right min-w-[200px] justify-center md:justify-end">
                    <div className="flex flex-col items-center md:items-end">
                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">US Cost</span>
                        <p className="text-md text-slate-400 line-through font-bold decoration-2 opacity-50 mb-1">{medicine.originalPrice}</p>
                    </div>

                    <div className="flex flex-col items-center md:items-end">
                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">Our Price</span>
                        <p className="text-4xl font-black text-green-600 leading-none tracking-tighter mb-1">{medicine.ourPrice}</p>
                        <div className="bg-green-100 text-green-700 text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-tighter">
                            {medicine.discount}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-row gap-3 w-full md:w-auto">
                    <Button
                        onClick={() => onWhatsAppClick(medicine)}
                        className="flex-1 md:flex-none bg-[#039855] hover:bg-[#027a48] text-white font-black h-14 px-10 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-green-100 transition-all hover:scale-105 active:scale-95 text-lg"
                    >
                        <MessageCircle className="w-6 h-6" />
                        <span>Inquiry</span>
                    </Button>
                    <Button
                        onClick={() => setIsExpanded(!isExpanded)}
                        variant="outline"
                        className={`flex-1 md:flex-none border-2 font-black h-14 px-6 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 text-md ${isExpanded ? 'border-red-200 bg-red-50 text-red-600' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                            }`}
                    >
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
                        <span>{isExpanded ? 'Hide Info' : 'View Info'}</span>
                    </Button>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="bg-slate-50/50 border-t border-slate-100 p-8 md:p-12 animate-in slide-in-from-top duration-500 fade-in">
                    <div className="grid md:grid-cols-2 gap-16 max-w-6xl">
                        {/* About Medicine */}
                        <div className="space-y-8">
                            <div className="flex items-center gap-4">
                                <div className="p-2.5 bg-red-100 rounded-xl">
                                    <Info className="w-6 h-6 text-red-600" />
                                </div>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em]">About Medication</h4>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
                                <div className="col-span-full">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 opacity-70">Medical Use</p>
                                    <p className="text-md text-slate-800 leading-relaxed font-semibold">
                                        {medicine.about?.use || 'Used for treatment as prescribed by a licensed physician.'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 opacity-70">Brand Reference</p>
                                    <p className="text-md text-red-600 font-black">
                                        {medicine.about?.brand || 'Generic Equivalent'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 opacity-70">Indication</p>
                                    <p className="text-md text-slate-800 font-black leading-tight">
                                        {medicine.about?.indication || 'Chronic Disease Treatment'}
                                    </p>
                                </div>
                                <div className="col-span-full">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 opacity-70">Common Side Effects</p>
                                    <p className="text-md text-slate-500 italic font-medium">
                                        {medicine.about?.sideEffects || 'Nausea, Fatigue, Loss of appetite.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Shipping Details */}
                        <div className="space-y-8">
                            <div className="flex items-center gap-4">
                                <div className="p-2.5 bg-green-100 rounded-xl">
                                    <Globe className="w-6 h-6 text-green-600" />
                                </div>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em]">Shipping Details</h4>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
                                <div className="flex items-start gap-4">
                                    <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm">
                                        <Truck className="w-5 h-5 text-[#039855]" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 opacity-70">Origin</p>
                                        <p className="text-md text-slate-900 font-black leading-none">{medicine.shipping?.origin || 'India'}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm">
                                        <PackageCheck className="w-5 h-5 text-[#039855]" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 opacity-70">Delivery Period</p>
                                        <p className="text-md text-slate-900 font-black leading-none">{medicine.shipping?.deliveryPeriod || '7-14 Days'}</p>
                                    </div>
                                </div>
                                <div className="col-span-full bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mt-2">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 opacity-70">Service Type</p>
                                    <div className="flex items-center gap-4">
                                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                        <p className="text-lg text-slate-900 font-black tracking-tight">
                                            {medicine.shipping?.service || 'Express Global Delivery (Tracked)'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Action */}
                    <div className="mt-12 flex justify-center border-t border-slate-100 pt-10">
                        <Button
                            onClick={() => onWhatsAppClick(medicine)}
                            className="bg-[#039855] hover:bg-[#027a48] text-white font-black h-16 px-16 rounded-2xl flex items-center justify-center gap-4 shadow-2xl shadow-green-100 transition-all hover:scale-105 active:scale-95 text-xl"
                        >
                            <MessageCircle className="w-8 h-8" />
                            <span>Order on WhatsApp</span>
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MedicineRow;
